import { Icon, Modal, Text } from '@pukka/ui-components';
import DOMPurify from 'dompurify';
import { Button } from 'flowbite-react';
import { marked } from 'marked';

interface TnCModalProps {
  readonly open?: boolean;
  readonly onToggleModal?: (open: boolean) => void;
}

function getUnsafeHTMLContent(content: string) {
  const markdown = DOMPurify.sanitize(marked.parse(content) as string);

  return { __html: markdown };
}

const TNC_CONTENT = [
  {
    content: `Mitra Usaha dapat menghapus Aplikasi MOKAPOS dari telepon genggam dan/atau tablet Mitra Usaha setiap saat. MOKA tidak
    memiliki kewajiban apa pun kepada Mitra Usaha terhadap hal-hal yang timbul sejak penghapusan Aplikasi MOKAPOS, pembekuan
    sementara atau pembekuan permanen Akun. Akan tetapi, Mitra Usaha tetap bertanggung jawab untuk menyelesaikan
    kewajiban-kewajiban Mitra Usaha yang telah timbul, termasuk namun tidak terbatas pada, setiap kewajiban yang mungkin timbul
    akibat adanya sengketa, tuntutan, maupun tindakan hukum lainnya yang telah ada, sebelum tanggal penghapusan Aplikasi
    MOKAPOS, pembekuan sementara atau pembekuan permanen Akun Mitra Usaha.`,
  },
  {
    content: 'Akun Mitra Usaha dapat dibekukan karena hal-hal, termasuk namun tidak terbatas pada, sebagai berikut:',
    subcontent: [
      'Melakukan pelanggaran atas Ketentuan Penggunaan ini dan/atau [Kebijakan Privasi](https://www.mokapos.com/kebijakan-privasi);',
      'Laporan Mitra Usaha bahwa Akun digunakan atau diduga digunakan atau disalahgunakan oleh pihak ketiga;',
      'Laporan Mitra Usaha bahwa telepon genggam atau tablet pribadi Mitra Usaha hilang, dicuri atau diretas;',
      'MOKA mengetahui atau mempunyai alasan yang cukup untuk menduga bahwa Akun telah dialihkan atau digunakan oleh pihak lain tanpa persetujuan terlebih dahulu dari MOKA;',
      'MOKA mengetahui atau dengan alasan yang cukup menduga bahwa telah terjadi hal-hal yang menurut pandangan MOKA telah atau dapat merugikan MOKA, Mitra Pihak Ketiga Penyedia Layanan, Penyedia Penawaran, atau pihak lainnya;',
      'Sistem MOKA mendeteksi adanya tindakan yang tidak wajar dari penggunaan Akun atau adanya kewajiban berdasarkan Ketentuan Penggunaan dan/atau [Kebijakan Privasi](https://www.mokapos.com/kebijakan-privasi) yang tidak dipenuhi oleh Mitra Usaha;',
      'Mitra Usaha tidak melakukan pembayaran atas biaya berlangganan selama jangka waktu yang ditentukan di dalam Platform MOKAPOS;',
      'Mitra Usaha telah meninggal dunia, ditempatkan di bawah perwalian atau pengampuan atau mengalami ketidakmampuan lainnya yang menjadikan Mitra Usaha tidak cakap hukum berdasarkan peraturan perundang-undangan yang berlaku;',
      'Dalam hal Mitra Usaha mengajukan atau diajukan oleh pihak lain permohonan kepada instansi yang berwenang untuk dinyatakan pailit atau untuk diberikan penundaan membayar hutang-hutang atau telah dinyatakan berada dalam keadaan pailit atau diberikan penundaan membayar hutang-hutang oleh instansi yang berwenang;',
      'Penggunaan Platform MOKAPOS dan/atau Layanan oleh Mitra Usaha atau pihak lain yang terotorisasi (yang menggunakan Akun) dengan cara yang bertentangan dengan Ketentuan Penggunaan ini, [Kebijakan Privasi](https://www.mokapos.com/kebijakan-privasi) atau peraturan perundang-undangan yang berlaku; dan/atau',
      'Perintah untuk pembekuan Akun yang diterbitkan oleh institusi pemerintah atau moneter terkait atau berdasarkan perintah pengadilan yang diterbitkan sesuai dengan peraturan perundang-undangan yang berlaku.',
    ],
  },
  {
    content:
      'Mitra Usaha dapat mengajukan penghapusan Akun kepada MOKA melalui kanal yang disediakan oleh MOKA. Pengajuan penghapusan Akun hanya dapat dilakukan oleh pemilik utama Akun yang terdaftar di sistem MOKA, dengan ketentuan sebagai berikut:',
    subcontent: [
      'MOKA akan memproses pengajuan penghapusan Akun dalam jangka waktu 30 Hari Kerja;',
      'MOKA berhak melakukan pembekuan Akun selama pemrosesan penghapusan Akun;',
      'MOKA berhak untuk menolak pengajuan penghapusan Akun yang diajukan oleh Mitra Usaha, termasuk namun tidak terbatas apabila Mitra Usaha masih memiliki sisa kewajiban kepada MOKA;',
      'MOKA akan memberikan pemberitahuan kepada Mitra Usaha terkait hasil dari pengajuan penghapusan Akun;',
      'Tunduk kepada [Kebijakan Privasi](https://www.mokapos.com/kebijakan-privasi), MOKA berhak untuk menyimpan informasi yang berkaitan dengan Mitra Usaha setelah penghapusan Akun disetujui oleh MOKA;',
    ],
  },
  {
    content: `Jika Akun dibekukan dan Mitra Usaha memiliki bukti yang jelas bahwa Akun seharusnya tidak dibekukan, 
    Mitra Usaha dapat membuat Laporan kepada MOKA untuk menyampaikan bukti-bukti tersebut. Setelah melakukan 
    pemeriksaan lebih lanjut terhadap Laporan Mitra Usaha, MOKA akan, atas kebijakan MOKA sepenuhnya, menentukan 
    untuk mengakhiri atau melanjutkan pembekuan terhadap Akun Mitra Usaha. Pembekuan tidak akan diteruskan secara 
    tidak wajar apabila MOKA memutuskan bahwa hal-hal yang mengakibatkan terjadinya pembekuan telah diselesaikan.`,
  },
];

function AccountDeletionTnC({ open, onToggleModal }: TnCModalProps) {
  return (
    <Modal unwrap open={open} onOpenChange={onToggleModal}>
      <Modal.Content className="bg-neutral-50">
        <Modal.Title className="flex-row justify-between">
          Syarat dan Ketentuan
          <Modal.Trigger asChild>
            <Button variant="nude" size="none" className="rounded-md">
              <Icon icon="close" color="white" />
            </Button>
          </Modal.Trigger>
        </Modal.Title>
        <Modal.Body className="my-0 max-h-128 p-6 text-justify">
          <Text variant="caption-1">
            <ol className="list-decimal pb-4 pl-4">
              {TNC_CONTENT.map((tnc, index) => (
                <li className="pb-4 pl-2" key={index}>
                  {tnc.content}
                  {tnc.subcontent ? (
                    <ol className="list-[lower-latin] pl-6">
                      {tnc.subcontent.map((c, idx) => (
                        <li
                          key={idx}
                          dangerouslySetInnerHTML={getUnsafeHTMLContent(c)}
                          className="pb-1 pl-2 [&>p>a]:text-blue-500 [&>p>a]:underline"
                        />
                      ))}
                    </ol>
                  ) : null}
                </li>
              ))}
            </ol>
          </Text>
        </Modal.Body>
      </Modal.Content>
    </Modal>
  );
}

export { AccountDeletionTnC };
